// Central PII / work-data scrubber for Sentry.
//
// Cydea is a quantitative cyber-risk platform: error messages, breadcrumbs, span
// attributes and exception values routinely contain data that must never leave the
// browser — scenario titles/descriptions, financial loss figures, incident text,
// control descriptions, organisation/workspace NAMES, and owner names/emails.
//
// `sendDefaultPii: false` only suppresses SDK-auto PII (IP address, cookies, request
// headers). It does NOT scrub application-supplied strings, URLs, request/response
// bodies, or the `graphql.document` span attribute. These three hooks are the single
// backstop every capture site inherits, independent of how a given call site behaves.
//
// Wired in src/index.tsx as beforeSend / beforeSendTransaction / beforeBreadcrumb.
import type { Event, Breadcrumb } from '@sentry/react';

// Free-text query-string params that can carry names/emails/search text. ID-bearing
// params (workspace_id, page, status, …) are intentionally NOT redacted — they are
// safe and useful for debugging.
const SENSITIVE_QUERY_PARAMS = ['workspace_name', 'name', 'email', 'search', 'q', 'title'];

// `extra` / context keys that have historically carried raw server bodies, SQL, stack
// traces, or echoed user input. Deleted defensively even if a call site regresses.
const SENSITIVE_EXTRA_KEYS = [
  'result',
  'bodyText',
  'response',
  'networkError',
  'extensions',
  'debugMessage',
  'trace',
  'variables',
  'data',
];

// Span attributes written by graphqlClientIntegration. `graphql.document` is the FULL
// raw request body (query + variables) — verified in the installed
// @sentry/browser .../integrations/graphqlClient.js. Stripping it keeps the operation
// name (carried in the span name) while dropping the variables.
const GRAPHQL_DOC_KEYS = ['graphql.document', 'graphql.operation'];

const redactUrlParams = (url?: string): string | undefined => {
  if (!url) return url;
  let out = url;
  for (const param of SENSITIVE_QUERY_PARAMS) {
    out = out.replace(new RegExp(`([?&]${param}=)[^&#]*`, 'gi'), `$1[redacted]`);
  }
  return out;
};

const stripExtra = (extra?: Record<string, unknown>): void => {
  if (!extra) return;
  for (const key of SENSITIVE_EXTRA_KEYS) delete extra[key];
};

const stripGraphqlDocument = (data?: Record<string, unknown> | null): void => {
  if (!data) return;
  for (const key of GRAPHQL_DOC_KEYS) delete data[key];
};

const redactBreadcrumbUrls = (data?: Record<string, unknown>): void => {
  if (!data) return;
  for (const key of ['url', 'to', 'from']) {
    if (typeof data[key] === 'string') data[key] = redactUrlParams(data[key] as string);
  }
};

// Generic over Event so the same body serves both beforeSend (ErrorEvent) and
// beforeSendTransaction (TransactionEvent) and returns the precise input type.
const scrubEvent = <T extends Event>(event: T): T => {
  // 1. URLs (request + referer header)
  if (event.request?.url) event.request.url = redactUrlParams(event.request.url);
  if (event.request?.headers) {
    const headers = event.request.headers as Record<string, string>;
    delete headers.Authorization;
    delete headers.authorization;
    if (headers.Referer) headers.Referer = redactUrlParams(headers.Referer) ?? '';
    if (headers.referer) headers.referer = redactUrlParams(headers.referer) ?? '';
  }
  // 2. request body / cookies (belt-and-braces over sendDefaultPii:false)
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
  }
  // 3. extra denylist
  stripExtra(event.extra);
  // 4. span attributes (GraphQL variables) on every span + the root trace context
  event.spans?.forEach((span) => stripGraphqlDocument(span.data));
  stripGraphqlDocument(event.contexts?.trace?.data);
  // 5. breadcrumb urls + any leftover graphql doc
  event.breadcrumbs?.forEach((crumb) => {
    redactBreadcrumbUrls(crumb.data);
    stripGraphqlDocument(crumb.data);
  });
  return event;
};

export function beforeSend<T extends Event>(event: T): T | null {
  return scrubEvent(event);
}

export function beforeSendTransaction<T extends Event>(event: T): T | null {
  return scrubEvent(event);
}

export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Console breadcrumbs are already disabled via breadcrumbsIntegration({console:false});
  // this drops any that slip through other categories and scrubs urls/graphql bodies.
  if (breadcrumb.category === 'console' && ['log', 'info', 'debug'].includes(breadcrumb.level ?? '')) {
    return null;
  }
  if (breadcrumb.data) {
    stripGraphqlDocument(breadcrumb.data);
    redactBreadcrumbUrls(breadcrumb.data);
  }
  return breadcrumb;
}
